'use client';

import { useEffect, useMemo, useState } from 'react';

/**
 * Syncs `.active` on section nav links as the user scrolls (legacy mCustomScrollbar scrollspy).
 */
export function useSectionScrollSpy(sectionIds: string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(sectionIds[0] || null);
  const sectionKey = useMemo(() => sectionIds.join('|'), [sectionIds]);
  const ids = useMemo(
    () => (sectionKey ? sectionKey.split('|').filter(Boolean) : []),
    [sectionKey],
  );

  useEffect(() => {
    if (ids.length === 0 || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const visibility = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.id;
          if (!id) {
            continue;
          }
          visibility.set(id, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        let bestId: string | null = null;
        let bestRatio = 0;
        for (const id of ids) {
          const ratio = visibility.get(id) || 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        }
        if (bestId) {
          setActiveId(bestId);
        }
      },
      {
        root: null,
        rootMargin: '-20% 0px -55% 0px',
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      },
    );

    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
      }
    }

    return () => {
      observer.disconnect();
    };
  }, [ids]);

  return activeId;
}
